import styled from '@emotion/styled';
import React from 'react';

import {
    PageFirstBackgroundImagePosition,
    PageSecondBackgroundImagePosition,
} from 'src/generated/webapp_gql';
import { mediaQueries } from 'src/shared/styles/variables';

export type BackgroundImagePosition =
    | PageFirstBackgroundImagePosition
    | PageSecondBackgroundImagePosition;

export interface Props {
    backgroundImageUrl?: string;
    backgroundImagePosition?: BackgroundImagePosition | null;
    testID?: string;
}

export enum BackgroundLayerTestID {
    SvgBackground = 'BackgroundLayerTestID.SVGBackground',
}

interface PositionProps {
    backgroundImagePosition?: BackgroundImagePosition | null;
}

const styles = {
    backgroundBottom: { alignItems: 'flex-end' },
    backgroundLeft: { justifyContent: 'flex-start' },
    backgroundCenter: { justifyContent: 'center' },
    backgroundRight: { justifyContent: 'flex-end' },
} as const;

const BackgroundImageContainer = styled.div<PositionProps>(
    {
        position: 'absolute',
        width: '100%',
        height: '100%',
        display: 'flex',
        marginLeft: '-1.5rem',
        marginRight: '-1.5rem',
        zIndex: -1,
        ...styles.backgroundBottom,
        [mediaQueries.md]: {
            display: 'none',
        },
    },
    ({ backgroundImagePosition }) => {
        switch (backgroundImagePosition) {
            case PageFirstBackgroundImagePosition.BottomLeft:
            case PageSecondBackgroundImagePosition.BottomLeft:
                return [styles.backgroundLeft];
            case PageFirstBackgroundImagePosition.BottomRight:
            case PageSecondBackgroundImagePosition.BottomRight:
                return [styles.backgroundRight];
            case PageFirstBackgroundImagePosition.BottomCenter:
            case PageSecondBackgroundImagePosition.BottomCenter:
            default:
                return [styles.backgroundCenter];
        }
    },
);

const BackgroundImage = styled.img<PositionProps>(
    {
        maxHeight: '40%',
        minHeight: '30%',
        maxWidth: '30%',
        minWidth: '20%',
        objectFit: 'contain',
    },
    ({ backgroundImagePosition }) => {
        switch (backgroundImagePosition) {
            case PageFirstBackgroundImagePosition.BottomLeft:
            case PageSecondBackgroundImagePosition.BottomLeft:
                return {
                    objectPosition: 'left bottom',
                };
            case PageFirstBackgroundImagePosition.BottomRight:
            case PageSecondBackgroundImagePosition.BottomRight:
                return {
                    objectPosition: 'right bottom',
                };
            case PageFirstBackgroundImagePosition.BottomCenter:
            case PageSecondBackgroundImagePosition.BottomCenter:
            default:
                return {
                    objectPosition: 'center bottom',
                };
        }
    },
);

export const PageBackground: React.FC<Props> = ({
    backgroundImageUrl,
    backgroundImagePosition = PageFirstBackgroundImagePosition.BottomCenter,
    testID,
}) => {
    if (!backgroundImageUrl) {
        return null;
    }

    return (
        <BackgroundImageContainer
            data-testid={testID}
            backgroundImagePosition={backgroundImagePosition}
        >
            <BackgroundImage
                backgroundImagePosition={backgroundImagePosition}
                src={backgroundImageUrl}
            />
        </BackgroundImageContainer>
    );
};
